import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import InfoIcon from '../icons/InfoIcon';
import Tooltip from '../atoms/Tooltip';
import Loader from '../atoms/Loader';
import { datetimeToDate } from '../services/converter';
import gql from '../services/gql';
import {
  COLOR_BLUE,
  COLOR_DARK_GRAY,
  COLOR_RED,
  COLOR_TEXT,
  COLOR_WHITE,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import PlayerUpdate, { PlayerUpdateType } from '../types/PlayerUpdate';
import { Typography } from '@material-ui/core';
import Player from '../types/Player';

interface UpdatedLastUpdatedProps {
  player?: Player;
  className?: string;
  playerId: number;
  datetime: string;
  hideTooltip?: boolean;
  iconClassName?: string;
}

const UPDATE_TYPE_NAME = {
  [PlayerUpdateType.WEIGHT]: 'Weight Updated',
  [PlayerUpdateType.HEIGHT]: 'Height Updated',
  [PlayerUpdateType.TRACK_DATA]: 'Track Data Updated',
  [PlayerUpdateType.PAI_SCORE]: 'PAI Score Updated',
  [PlayerUpdateType.COMBINE_SCORE]: 'Combine Score Updated',
  [PlayerUpdateType.STAR_RATING]: 'Star Rating Changed',
  [PlayerUpdateType.OFFERS]: 'New Offers',
  [PlayerUpdateType.COMMITTED]: 'Committed',
  [PlayerUpdateType.COMBINE_DATA]: 'New Combine Data',
  [PlayerUpdateType.PPI]: 'PPI Score Updated',
  [PlayerUpdateType.WATCH_LIST]: 'Watch list Updated',
  [PlayerUpdateType.ALL_AMERICAN]: 'All American Updated',
  [PlayerUpdateType.ALL_CONFERENCE]: 'All Conference Updated',
  [PlayerUpdateType.MPH_DATA]: 'MPH Data',
  [PlayerUpdateType.PRODUCTION_DATA]: 'Production Data Updated',
  [PlayerUpdateType.LINKS_AND_KEYS]: 'Links and Keys Updated',
  [PlayerUpdateType.DEPTHCHART]: 'Depthchart Updated',
  [PlayerUpdateType.CONTACT_INFO]: 'Contact Info Updated',
  [PlayerUpdateType.MULTISPORT]: 'Multisport Updated',
  [PlayerUpdateType.NEW]: 'New Player',
  [PlayerUpdateType.TEAM_CHANGE]: 'Team Change Updated',
};

const useStyles = makeStyles((theme: Theme) => ({
  UpdatedLastUpdated: {
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    color: COLOR_DARK_GRAY,
  },
  text: {
    paddingRight: '8px',
  },
  icon: {
    color: COLOR_TEXT,
    transition: 'color 0.3s',
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_BLUE,
    }
  },

  tooltipContent: {
    minWidth: 100,
    minHeight: 50,
    position: 'relative',
  },

  loader: {
    background: COLOR_TEXT,
  },

  noUpdates: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(2),
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1,
  },

  table: {
    borderCollapse: 'collapse',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(15),
    marginBottom: `${theme.spacing(0.75)}px`,
    marginTop: `${theme.spacing(0.75)}px`,
  },
  tableRow: {},
  tableCell: {
    color: COLOR_WHITE,
    fontSize: theme.typography.pxToRem(14),
    padding: `${theme.spacing(0.25)}px 
              ${theme.spacing(1)}px
              ${theme.spacing(0.25)}px 
              ${theme.spacing(0)}px
              `,

    '&:first-of-type': {
      fontWeight: 'bold',
      textAlign: 'left',
      whiteSpace: 'nowrap'
    },
  },
  updateHeading: {
    fontWeight: 600,
    marginBottom: 0,
  },
  createdDate: {
    margin: `${theme.spacing(1)}px ${theme.spacing(0)}px`
  },
  aboutPlayer: {
    margin: `${theme.spacing(1)}px ${theme.spacing(0)}px`
  },
  updateRow: {
    display: 'flex',
  },
  date: {
    width: 100,
  },
}), { name: UpdatedLastUpdated.name });

export default function UpdatedLastUpdated(props: UpdatedLastUpdatedProps) {
  const {
    className,
    playerId,
    hideTooltip = false,
    datetime,
    iconClassName,
    player
  } = props;
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [playerUpdates, setPlayerUpdates] = useState<PlayerUpdate[] | null>(null);

  function getAboutPlayer(updates: PlayerUpdate[] | null): string {
    if (!updates?.length) {
      return '';
    }

    const updateWithAboutPlayer = updates.find((update) => typeof update?.eventDetails?.aboutPlayer === 'string' && update.eventDetails.aboutPlayer.trim());
    if (updateWithAboutPlayer) {
      return String(updateWithAboutPlayer.eventDetails.aboutPlayer);
    }

    const updateWithPreviousTeams = updates.find((update) => Array.isArray(update?.eventDetails?.previousTeams) && update.eventDetails.previousTeams.length);
    if (updateWithPreviousTeams) {
      const previousTeams = updateWithPreviousTeams.eventDetails.previousTeams as string[];
      return `Previously attended ${previousTeams.join(' & ')}`;
    }

    const updateWithOffers = updates.find((update) => Array.isArray(update?.eventDetails?.offers) && update.eventDetails.offers.length);
    if (updateWithOffers) {
      const offers = updateWithOffers.eventDetails.offers as string[];
      const visibleOffers = offers.slice(0, 3).join(', ');
      const remainingCount = Math.max(offers.length - 3, 0);

      if (remainingCount > 0) {
        return `Offers: ${visibleOffers} +${remainingCount} more`;
      }

      return `Offers: ${visibleOffers}`;
    }

    return '';
  }


  function fetchPlayerUpdates () {
    setLoading(true);

    gql(`
      playerUpdatesTooltip(playerId:${playerId}, limit:10) {
        id
        type
        createdAt
        eventDetails
        createdAt
        updatedAt
      }
    `)
      .then((data:any) => data.playerUpdatesTooltip as PlayerUpdate[])
      .then((playerUpdates:PlayerUpdate[]) => {
        setPlayerUpdates(playerUpdates || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function onOpen() {
    setOpen(true);

    if (!playerUpdates) {
      fetchPlayerUpdates();
    }
  }

  const aboutPlayer = player?.about;

  return (
    <div
      className={clsx(classes.UpdatedLastUpdated, className)}
      onClick={hideTooltip ? undefined : onOpen}
    >
      <span className={classes.text}>
        Last Updated: {datetimeToDate(datetime)}
      </span>

      {!hideTooltip && (
        <Tooltip
          open={open}
          interactive={true}
          title={(
            <div className={classes.tooltipContent}>
              <Loader
                className={classes.loader}
                inProgress={loading}
              />

              {(!loading && !(playerUpdates && playerUpdates.length)) && (
                <div className={classes.noUpdates}>No Updates</div>
              )}

              {(!loading && !!playerUpdates?.length) && (
                <>
                  <Typography className={classes.createdDate} variant='body2' gutterBottom>
                    <b>Created Date: </b>
                    {datetimeToDate(player?.createdAt || playerUpdates[0]?.createdAt)}
                  </Typography>
                  {!!aboutPlayer && (
                    <Typography className={classes.aboutPlayer} variant='body2'>
                      <b>About Player: </b>
                      {aboutPlayer}
                    </Typography>
                  )}
                  <Typography className={classes.updateHeading}>Updates:</Typography>
                  {(playerUpdates ?? []).map(
                    (data: PlayerUpdate) => (
                      <Typography
                        key={`${data.createdAt}-${data.type}-${data.id}`}
                        variant="body2"
                        className={classes.updateRow}
                      >
                        <span className={classes.date}>{datetimeToDate(data.createdAt)}</span>
                        <b>{UPDATE_TYPE_NAME[data.type] || data.type}</b>
                      </Typography>
                    )
                  )}
                </>
              )}
            </div>
          )}
          onClose={() => setOpen(false)}
          placement='bottom'
        >
          <InfoIcon className={clsx(classes.icon, iconClassName)} />
        </Tooltip>
      )}
    </div>
  );
}
